import picsioUtils from '@picsio/utils';
import picsioConfig from '../../../../../../config';
import l10n from '../../../shared/strings';
import Logger from '../../../services/Logger';
import GDriveUploader from '../../../storage/GDriveUploader';
import buildFileChunks from '../../../storage/buildFileChunks';

const GD_CHUNK_SIZE = 64 * 1024 * 1024; // 64MB, multiple of 256KiB
const COMPLETE_RETRY_ATTEMPTS = 3;
const S3_PART_CONCURRENCY = 5;
const S3_PART_TIMEOUT = 1000 * 60 * 30; // 30m per part

export default function uploadFile(file, additionalFields, onUploadProgress = () => {}, signal) {
  /** Normalize decomposed symbols into composed, as the main app does */
  const fileName = file.name.normalize('NFC');
  const contentType = detectContentType(file, fileName);
  const inboxApiUrl = window.inbox.uploadUrl || picsioConfig.inboxesBaseUrl || '';
  const inboxId = window.inbox._id;

  return requestUploadGrant(inboxApiUrl, inboxId, {
    title: fileName,
    contentLength: file.size,
    contentType,
    additionalFields,
  }, signal)
    .then((grant) => {
      if (grant.useProxy) {
        return proxyUpload(file, fileName, additionalFields, onUploadProgress, signal, inboxApiUrl, inboxId, contentType);
      }

      if (grant.storageType === 'gd') {
        return uploadToGoogleDrive(file, grant, contentType, onUploadProgress, signal)
          .then((uploadedFile) => completeUpload(inboxApiUrl, inboxId, {
            storageType: 'gd',
            fileId: uploadedFile.id,
            fileName: grant.fileName || fileName,
            contentLength: file.size,
            contentType,
            additionalFields,
          }, signal));
      }

      if (grant.storageType === 's3' || grant.storageType === 'picsio') {
        return uploadToS3(file, grant, contentType, onUploadProgress, signal)
          .then((result) => completeUpload(inboxApiUrl, inboxId, {
            storageType: grant.storageType,
            storageId: grant.storageId,
            uploadId: grant.uploadId,
            parts: result.parts,
            fileName: grant.fileName || fileName,
            contentLength: file.size,
            contentType,
            additionalFields,
            revisionId: grant.revisionId,
          }, signal));
      }

      return proxyUpload(file, fileName, additionalFields, onUploadProgress, signal, inboxApiUrl, inboxId, contentType);
    });
}

function detectContentType(file, fileName) {
  const type = typeof file.type === 'string' ? file.type.trim().toLowerCase() : '';
  if (type && type !== 'application/octet-stream') return type;
  return picsioUtils.lookupMimeType(fileName.split('.').pop().toLowerCase()) || 'application/octet-stream';
}

function requestUploadGrant(inboxApiUrl, inboxId, body, signal) {
  return fetch(`${inboxApiUrl}/upload-link/${inboxId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  }).then(async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw buildResponseError(data.message || `Upload link failed with status ${response.status}`, response.status, data);
    }
    return data;
  }, (err) => {
    throw normalizeAbort(err);
  });
}

function completeUpload(inboxApiUrl, inboxId, body, signal, attempt = 1) {
  const retry = async () => {
    await delay(250 * (2 ** attempt));
    return completeUpload(inboxApiUrl, inboxId, body, signal, attempt + 1);
  };

  return fetch(`${inboxApiUrl}/complete/${inboxId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  }).then(
    async (response) => {
      if (response.ok) return response.json().catch(() => ({}));

      const data = await response.json().catch(() => ({}));
      /** Only server-side failures are worth retrying — /complete is idempotent */
      if (attempt < COMPLETE_RETRY_ATTEMPTS && response.status >= 500) return retry();

      throw buildResponseError(data.message || `Complete upload failed with status ${response.status}`, response.status, data);
    },
    (err) => {
      const normalized = normalizeAbort(err);
      if (normalized.isCancelled) throw normalized;
      /** Network error: the request may well have succeeded, so retry the idempotent call */
      if (attempt < COMPLETE_RETRY_ATTEMPTS) return retry();
      throw normalized;
    },
  );
}

function uploadToGoogleDrive(file, grant, contentType, onUploadProgress, signal) {
  return new Promise((resolve, reject) => {
    let handleAbort;

    const finish = (fn) => (value) => {
      if (signal && handleAbort) signal.removeEventListener('abort', handleAbort);
      fn(value);
    };
    const settleResolve = finish(resolve);
    const settleReject = finish(reject);

    const uploader = new GDriveUploader({
      file,
      url: grant.url,
      token: grant.token,
      chunkSize: GD_CHUNK_SIZE,
      contentType,
      metadata: {
        title: grant.fileName || file.name,
        mimeType: contentType,
      },
      onComplete: (response) => {
        try {
          settleResolve(typeof response === 'string' ? JSON.parse(response) : response);
        } catch (err) {
          settleReject(err);
        }
      },
      onError: (response) => settleReject(buildUploaderError(response)),
      onProgress: (loaded, total, xhr) => {
        onUploadProgress({
          percentage: total ? (loaded / total) * 100 : 0,
          xhr,
          total: loaded,
        });
      },
    });

    handleAbort = () => {
      uploader.abort();
      settleReject(cancelledError());
    };

    if (signal) {
      if (signal.aborted) {
        handleAbort();
        return;
      }
      signal.addEventListener('abort', handleAbort, { once: true });
    }

    uploader.upload().catch(settleReject);
  });
}

async function uploadToS3(file, grant, contentType, onUploadProgress, signal) {
  const { urls: urlsData, chunkNums } = grant;
  const fileBlobChunks = buildFileChunks(file, chunkNums, contentType);

  const uploadProgress = {};
  const activeXhrs = new Set();
  let uploadAborted = false;

  const handleAbort = () => {
    uploadAborted = true;
    activeXhrs.forEach((xhr) => xhr.abort());
  };

  if (signal) {
    if (signal.aborted) throw cancelledError();
    signal.addEventListener('abort', handleAbort);
  }

  try {
    const parts = [];
    for (let i = 0; i < urlsData.length; i += S3_PART_CONCURRENCY) {
      const batch = urlsData.slice(i, i + S3_PART_CONCURRENCY);
      // eslint-disable-next-line no-await-in-loop
      const batchResults = await Promise.all(batch.map(async (urlObject, batchIndex) => {
        const index = i + batchIndex;
        if (uploadAborted) throw cancelledError();

        const chunk = fileBlobChunks[urlObject.partNum - 1];
        const etag = await putS3Part(urlObject.url, chunk, {
          activeXhrs,
          isAborted: () => uploadAborted,
          onProgress: (loaded, total, xhr) => {
            uploadProgress[index] = (loaded / chunkNums / total) * 100;
            const percentage = Object.values(uploadProgress).reduce((sum, num) => sum + num, 0);
            onUploadProgress({
              percentage,
              xhr,
              total: (percentage / 100) * file.size,
            });
          },
        });

        return { ETag: etag, PartNumber: urlObject.partNum };
      }));
      parts.push(...batchResults);
    }

    return { parts: parts.sort((a, b) => a.PartNumber - b.PartNumber) };
  } finally {
    if (signal) signal.removeEventListener('abort', handleAbort);
  }
}

function putS3Part(url, chunk, options, maxTries = 3, tryNumber = 1) {
  const { activeXhrs, isAborted, onProgress } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    activeXhrs.add(xhr);

    const settle = (fn) => (value) => {
      activeXhrs.delete(xhr);
      fn(value);
    };
    const done = settle(resolve);
    const fail = settle(reject);

    xhr.upload.onprogress = ({ loaded, total }) => onProgress(loaded, total, xhr);
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        fail(buildResponseError(`Part upload failed with status ${xhr.status}`, xhr.status));
        return;
      }
      const etag = xhr.getResponseHeader('etag');
      if (!etag) {
        fail(new Error('Error getting ETag from response'));
        return;
      }
      done(etag);
    };
    xhr.onabort = () => fail(cancelledError());
    xhr.onerror = () => fail(new Error('Network error while uploading file part'));
    xhr.ontimeout = () => fail(new Error('Timeout while uploading file part'));
    xhr.open('PUT', url, true);
    xhr.timeout = S3_PART_TIMEOUT;
    xhr.setRequestHeader('content-disposition', 'attachment');
    xhr.send(chunk);
  }).catch(async (err) => {
    if (err.isCancelled || isAborted()) throw err;
    if (tryNumber >= maxTries) throw err;
    await delay(50 * (2 ** tryNumber));
    return putS3Part(url, chunk, options, maxTries, tryNumber + 1);
  });
}

function proxyUpload(file, fileName, additionalFields, onUploadProgress, signal, inboxApiUrl, inboxId, contentType) {
  const url = `${inboxApiUrl}/upload/${inboxId}?title=${encodeURIComponent(fileName)}&contentLength=${file.size}&contentType=${encodeURIComponent(contentType)}`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('additionalFields', JSON.stringify(additionalFields));

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const handleAbort = () => xhr.abort();
    const settle = (fn) => (value) => {
      if (signal) signal.removeEventListener('abort', handleAbort);
      fn(value);
    };
    const done = settle(resolve);
    const fail = settle(reject);

    if (signal) {
      if (signal.aborted) {
        fail(cancelledError());
        return;
      }
      signal.addEventListener('abort', handleAbort);
    }

    xhr.upload.onprogress = ({ loaded, total }) => onUploadProgress({ percentage: (loaded / total) * 100, xhr, total });
    xhr.onload = (res) => {
      if (res.target.status === 200) {
        done();
      } else {
        fail(buildResponseError(`Upload failed with status ${res.target.status}`, res.target.status));
      }
    };
    xhr.onabort = () => fail(cancelledError());
    xhr.onerror = (response) => {
      if (response.error && response.error.status === 500) {
        Logger.error(new Error('Import: internal server error'), { error: response }, [
          'ImportBadResponseFromDB',
          { errorMessage: (response.error && response.error.message) || 'NoMessage' },
        ]);
        const err500 = new Error(l10n.IMPORT.textCantSaveImageToDB);
        err500.code = 500;
        err500.reason = 'internalServerError';
        fail(err500);
      } else {
        Logger.error(new Error('Import: bad response from storage'), { error: response }, [
          'ImportBadResponseFromStorage',
          { errorMessage: 'Import: bad response from storage' },
        ]);
        const err403 = new Error(l10n.IMPORT.textCantSaveImageToStorage);
        err403.code = 403;
        err403.reason = 'badResponse';
        fail(err403);
      }
    };
    xhr.open('PUT', url, true);
    xhr.timeout = 1000 * 60 * 120; // 2h
    xhr.send(formData);
  });
}

function buildResponseError(message, status, data = {}) {
  const err = new Error(message);
  err.response = { status, ...data };
  err.status = status;
  return err;
}

/** GDriveUploader reports failures as plain objects, callers here expect an Error */
function buildUploaderError(response) {
  if (response instanceof Error) return response;
  if (response && response.reason === 'xhrAborted') return cancelledError();

  const err = new Error((response && response.message) || 'Upload to storage failed');
  if (response) {
    err.status = response.code;
    err.reason = response.reason;
    err.response = response;
  }
  return err;
}

function cancelledError() {
  const err = new Error('Upload cancelled');
  err.isCancelled = true;
  return err;
}

function normalizeAbort(err) {
  if (err && err.name === 'AbortError') return cancelledError();
  return err;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
